import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import { ServerSummary } from '../types/models';
import { ApiResponseServerList } from '../types/api/ApiResponseServer';

interface UseServersReturn {
  servers: ServerSummary[];
  count: number;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useServers = (limit = 25, offset = 0, name = '', role = ''): UseServersReturn => {
  const [servers, setServers] = useState<ServerSummary[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchServers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      if (name.trim()) {
        params.set('name', name.trim());
      }
      if (role.trim()) {
        params.set('role', role.trim());
      }

      const response = await apiFetch(`/datacenters/servers?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseServerList;
      if (data.status === 'success' && data.data?.servers) {
        setServers(data.data.servers);
        setCount(data.data.count);
      } else {
        setServers([]);
        setCount(0);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An error occurred while fetching servers'));
    } finally {
      setLoading(false);
    }
  }, [apiFetch, limit, offset, name, role]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchServers();
    }
  }, [auth.isAuthenticated, fetchServers]);

  return { servers, count, loading, error, refetch: fetchServers };
};
